import React, { forwardRef } from "react";
import cn from "classnames";
import { useComboxContext } from "~/components/shared/Combox/ComboxContext";

export interface ComboxFilterInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  placeholder?: string;
  className?: string;
  gridColumns?: number;
}

export const ComboxFilterInput = forwardRef<HTMLInputElement, ComboxFilterInputProps>(
  ({ placeholder = "Search...", className, gridColumns, onKeyDown, ...props }, ref) => {
    const {
      searchQuery,
      onSearchChange,
      searchInputRef,
      setOpen,
      highlightedIndex,
      setHighlightedIndexWithMethod,
      contentRef,
    } = useComboxContext();

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = event;

      if (key === "Escape") {
        setOpen(false);
        return;
      }

      const focusListWithLeftRightArrows =
        gridColumns && (key === "ArrowLeft" || key === "ArrowRight");

      if (
        key === "ArrowDown" ||
        key === "ArrowUp" ||
        key === "Enter" ||
        focusListWithLeftRightArrows
      ) {
        event.preventDefault();
        event.stopPropagation();

        // Get currently visible items
        const items = contentRef.current?.querySelectorAll("[data-combox-item]");
        if (!items || items.length === 0) {
          return;
        }

        // Helper function to check if an item is disabled
        const isItemDisabled = (index: number): boolean => {
          const item = items[index] as HTMLElement;
          return (
            item?.getAttribute("aria-disabled") === "true" ||
            item?.hasAttribute("disabled") ||
            item?.classList.contains("opacity-50")
          );
        };

        // Helper function to find next enabled item
        const findNextEnabledIndex = (startIndex: number): number => {
          for (let index = startIndex; index < items.length; index++) {
            if (!isItemDisabled(index)) {
              return index;
            }
          }

          return -1; // No enabled items found
        };

        // Helper function to find previous enabled item
        const findPrevEnabledIndex = (startIndex: number): number => {
          for (let index = startIndex; index >= 0; index--) {
            if (!isItemDisabled(index)) {
              return index;
            }
          }

          return -1; // No enabled items found
        };

        if (key === "ArrowDown") {
          if (gridColumns && highlightedIndex >= 0) {
            // Grid navigation: move down by gridColumns positions
            const targetIndex = Math.min(highlightedIndex + gridColumns, items.length - 1);
            setHighlightedIndexWithMethod(targetIndex, "keyboard");
          } else {
            // Standard navigation: next item
            const startIndex = highlightedIndex < 0 ? 0 : highlightedIndex + 1;
            const nextIndex = findNextEnabledIndex(startIndex);
            if (nextIndex !== -1) {
              setHighlightedIndexWithMethod(nextIndex, "keyboard");
            }
          }
        } else if (key === "ArrowUp") {
          if (gridColumns && highlightedIndex >= 0) {
            // Grid navigation: move up by gridColumns positions
            const targetIndex = Math.max(highlightedIndex - gridColumns, 0);
            setHighlightedIndexWithMethod(targetIndex, "keyboard");
          } else {
            // Standard navigation: previous item
            const startIndex = highlightedIndex < 0 ? items.length - 1 : highlightedIndex - 1;
            const prevIndex = findPrevEnabledIndex(startIndex);
            if (prevIndex !== -1) {
              setHighlightedIndexWithMethod(prevIndex, "keyboard");
            }
          }
        } else if (key === "ArrowLeft" && gridColumns) {
          const startIndex = highlightedIndex < 0 ? items.length - 1 : highlightedIndex - 1;
          const prevIndex = findPrevEnabledIndex(startIndex);
          if (prevIndex !== -1) {
            setHighlightedIndexWithMethod(prevIndex, "keyboard");
          }
        } else if (key === "ArrowRight" && gridColumns) {
          const startIndex = highlightedIndex < 0 ? 0 : highlightedIndex + 1;
          const nextIndex = findNextEnabledIndex(startIndex);
          if (nextIndex !== -1) {
            setHighlightedIndexWithMethod(nextIndex, "keyboard");
          }
        } else if (key === "Enter" && highlightedIndex >= 0) {
          // Only allow selection of enabled items
          if (!isItemDisabled(highlightedIndex)) {
            const selectedItem = items[highlightedIndex] as HTMLElement;
            selectedItem?.click();
          }
        }
      }

      onKeyDown?.(event);
    };

    const inputRef = ref || searchInputRef;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      onSearchChange(event.target.value);

    const handleClear = () => {
      onSearchChange("");
      (inputRef as React.RefObject<HTMLInputElement>)?.current?.focus();
    };

    return (
      <div className="bg-white p-2 pb-0">
        <div className="relative flex items-center">
          <input
            {...props}
            ref={inputRef}
            type="text"
            className={cn("input w-full px-2", searchQuery && "pr-7", className)}
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute right-2 flex h-5 w-5 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              onClick={handleClear}
              aria-label="Clear"
              data-testid="combox-filter-clear"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <path d="M1 1l8 8M9 1l-8 8" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  },
);

export default ComboxFilterInput;
